// import { firstLevelRoleTypes, secondLevelRoleTypes, roleTypes } from '../cicero/roles.js';
import { setValue, deepcopy, MapRescale, makeFilterFunction, toFilterString, optionOperation, evalExpression } from './utility.js';
import { DataTransformation, DataTransformTransformation } from './data.js';
import { ViewTransformation, ViewFacetTransformation, ViewFacetHeaderTransformation } from './view.js';
import { RCTransformation } from './rc.js';
import { LayerTransformation } from './layer.js';
import { MarkTransformation, MarkLabelTransformation, MarkLabelTickTransformation } from './mark.js';
import { AxisTransformation, AxisLabelTransformation, AxisTickTransformation, AxisGridTransformation } from './axis.js';
import { LegendTransformation } from './legend.js';
import { InteractionTransformation, TooltipTransformation } from './interaction.js';
import { TitleTransformation } from './title.js';
import { AnnotationTransformation } from './annotation.js';
import { EmphasisTransformation } from './emphasis.js';
import { LockTransformation } from "./lock.js";

let CFC = {
  // Utility functions
  setValue,
  deepcopy,
  MapRescale,
  makeFilterFunction,
  toFilterString,
  optionOperation,
  evalExpression,
  // transformation functions
  DataTransformation,
  DataTransformTransformation,
  ViewTransformation,
  ViewFacetTransformation,
  ViewFacetHeaderTransformation,
  RCTransformation,
  LayerTransformation,
  MarkTransformation,
  MarkLabelTransformation,
  MarkLabelTickTransformation,
  AxisTransformation,
  AxisLabelTransformation,
  AxisTickTransformation,
  AxisGridTransformation,
  LegendTransformation,
  InteractionTransformation,
  TooltipTransformation,
  TitleTransformation,
  AnnotationTransformation,
  EmphasisTransformation,
  LockTransformation
};

async function compilerCiceroToFronto(ciceroSpec, frontoSpec) {
  const propChannels = ["color", "stroke", "fillOpacity", "strokeOpacity", "opacity",
    "shape", "strokeDash", "strokeWidth", "size", "url", "fill",
    "angle", "theta", "radius", "radius2", "detail"];
  // Taking a @ciceroSpec and @frontoSpec, returns the transformed fronto spec
  // This grammar-specific compiler assumes a stable specs, which can be compiled in the order provided.
  let transformedSpec = JSON.parse(JSON.stringify(frontoSpec));
  let projectionScaleManual = true;
  transformedSpec.name = ciceroSpec.name.replace(/\s/g, "-");
  transformedSpec.data = CFC.deepcopy(transformedSpec.data).map(d => {
    Object.keys(d).forEach(dkey => {
      if (dkey.startsWith("$hl_")) {
        let ks = dkey.split("_");
        if (propChannels.includes(ks[ks.length - 1])) {
          delete d[dkey];
        }
      }
    });
    return d;
  });
  let lowPriorityRules = ciceroSpec.transformations.filter(rule => rule.important === undefined || rule.important == false);
  let highPriorityRules = ciceroSpec.transformations.filter(rule => rule.important == true);
  let prioritizedRules = highPriorityRules.length > 0 ? lowPriorityRules.concat(highPriorityRules) : lowPriorityRules;

  await prioritizedRules.forEach(async (rule) => {
    if (rule.specifier !== undefined && rule.action !== undefined) {
      // standard case
      let specifier;
      if (rule.specifier.constructor.name === "Object") {
        specifier = [rule.specifier];
      } else if (rule.specifier.constructor.name === "Array") {
        specifier = rule.specifier;
      }
      let option;
      if (rule.option?.constructor.name === "Object") {
        option = [rule.option];
      } else if (rule.option?.constructor.name === "Array") {
        option = rule.option;
      }
      await specifier.forEach(async specifierItem => {
        if (option !== undefined || option?.length > 0) {
          await option.forEach(async optionItem => {
            transformedSpec = await FCTransformation(transformedSpec, frontoSpec, specifierItem, rule.action, optionItem, rule.description);
            if (option.hasOwnProperty("projection")) {
              if (option.projection.scale !== undefined || option.projection.translate !== undefined) projectionScaleManual = false;
            }
          });
        } else {
          transformedSpec = await FCTransformation(transformedSpec, frontoSpec, specifierItem, rule.action, undefined, rule.description);
        }
      });
    }
  });

  if (projectionScaleManual && transformedSpec.layout?.composition === "projection" && frontoSpec.layout?.projection?.translate !== undefined) {
    transformedSpec = await CFC.MapRescale(transformedSpec, frontoSpec);
  }
  if (transformedSpec.layer !== undefined) {
    transformedSpec.layer = transformedSpec.layer.filter(d => Object.keys(d).length > 0);
  }
  return transformedSpec;
}

async function FCTransformation(spec, original, specifier, action, option, desc) {
  let role = specifier.role;
  // if (firstLevelRoleTypes.includes(specifier.role) && option?.role === undefined) { // specifier
  //   role = specifier.role;
  // } else if (firstLevelRoleTypes.includes(specifier?.role) && option?.role === undefined) { // option case 1 (role is not present in option)
  //   role = specifier.role;
  // } else if (firstLevelRoleTypes.includes(specifier?.role) && roleTypes.includes(option?.role)) { // option case 2 (role is present in option)
  //   role = specifier.role + "." + option.role;
  // }
  if (role && action === "lock") {
    // todo: lock transformation
    spec = await CFC.LockTransformation(role, spec, original, specifier, action, option);
  } else if (role) {
    if (["data"].includes(role)) {
      spec = await CFC.DataTransformation(role, spec, original, specifier, action, option);
    } else if (["transform", "data.transform"].includes(role)) {
      spec = await CFC.DataTransformTransformation(role, spec, original, specifier, action, option);
    } else if (["view"].includes(role)) {
      // single objet; possible action: modify only (for now)
      spec = await CFC.ViewTransformation(role, spec, original, specifier, action, option);
    } else if (["view.row", "row"].includes(role)) {
      spec = await CFC.RCTransformation(role, spec, original, specifier, action, option, "row");
    } else if (["view.column", "column"].includes(role)) {
      spec = await CFC.RCTransformation(role, spec, original, specifier, action, option, "column");
    } else if (["view.facet", "facet"].includes(role)) {
      spec = await CFC.ViewFacetTransformation(role, spec, original, specifier, action, option);
    } else if (["view.facet.title", "facet.title", "view.facet.text", "facet.text", "view.facet.label", "facet.label"].includes(role)) {
      spec = await CFC.ViewFacetHeaderTransformation(role, spec, original, specifier, action, option);
    } else if (["view.axis", "axis"].includes(role) || ["view.hAxis", "hAxis"].includes(role) || ["view.vAxis", "vAxis"].includes(role)) {
      spec = await CFC.AxisTransformation(role, spec, original, specifier, action, option);
    } else if (["view.hAxis", "hAxis"].includes(role)) {

    } else if (["view.vAxis", "vAxis"].includes(role)) {

    } else if (["axis.domain", "view.axis.domain"].includes(role)) {

    } else if (["vAxis.domain", "view.vAxis.domain"].includes(role)) {

    } else if (["hAxis.domain", "view.hAxis.domain"].includes(role)) {

    } else if (["axis.grid", "view.axis.grid"].includes(role) || ["vAxis.grid", "view.vAxis.grid"].includes(role) || ["hAxis.grid", "view.hAxis.grid"].includes(role)) {
      spec = await CFC.AxisGridTransformation(role, spec, original, specifier, action, option);
    } else if (["vAxis.grid", "view.vAxis.grid"].includes(role)) {

    } else if (["hAxis.grid", "view.hAxis.grid"].includes(role)) {

    } else if (["axis.tick", "view.axis.tick"].includes(role) || ["vAxis.tick", "view.vAxis.tick"].includes(role) || ["hAxis.tick", "view.hAxis.tick"].includes(role)) {
      spec = await CFC.AxisTickTransformation(role, spec, original, specifier, action, option);
    } else if (["vAxis.tick", "view.vAxis.tick"].includes(role)) {

    } else if (["hAxis.tick", "view.hAxis.tick"].includes(role)) {

    } else if (["axis.title", "view.axis.title", "text.axis.title"].includes(role)) {

    } else if (["vAxis.title", "view.vAxis.title", "text.vAxis.title"].includes(role)) {

    } else if (["hAxis.title", "view.hAxis.title", "text.hAxis.title"].includes(role)) {

    } else if (["axis.label", "view.axis.label", "text.axis.label"].includes(role) || ["vAxis.label", "view.vAxis.label", "text.vAxis.label"].includes(role) || ["hAxis.label", "view.hAxis.label", "text.hAxis.label"].includes(role)) {
      spec = await CFC.AxisLabelTransformation(role, spec, original, specifier, action, option);
    } else if (["vAxis.label", "view.vAxis.label", "text.vAxis.label"].includes(role)) {

    } else if (["hAxis.label", "view.hAxis.label", "text.hAxis.label"].includes(role)) {

    } else if (["layer"].includes(role)) {
      spec = await CFC.LayerTransformation(role, spec, original, specifier, action, option);
    } else if (["layer.transform"].includes(role)) {

    } else if (["layer.mark", "mark"].includes(role)) {
      spec = await CFC.MarkTransformation(role, spec, original, specifier, action, option);
    } else if (["layer.mark.label", "mark.label", "text.mark"].includes(role)) {
      spec = await CFC.MarkLabelTransformation(role, spec, original, specifier, action, option);
    } else if (["layer.mark.label.tick", "mark.label.tick"].includes(role)) {
      spec = await CFC.MarkLabelTickTransformation(role, spec, original, specifier, action, option);
    } else if (["mark.tooltip", "tooltip", "interaction.tooltip"].includes(role)) {
      spec = await CFC.TooltipTransformation(role, spec, original, specifier, action, option);
    } else if (["mark.tooltip.text", "tooltip.text", "interaction.tooltip.text", "text.tooltip"].includes(role)) {

    } else if (["layer.legend", "legend"].includes(role)) {
      spec = await CFC.LegendTransformation(role, spec, original, specifier, action, option);
    } else if (["layer.legend.mark", "legend.mark"].includes(role)) {
      // not yet supported by Fronto (uses defaults)

    } else if (["layer.legend.label", "legend.label", "text.legend.label"].includes(role)) {

    } else if (["layer.legend.title", "legend.title", "text.legend.title"].includes(role)) {

    } else if (["annotation", "text.annotation", "view.annotation"].includes(role)) {
      spec = await CFC.AnnotationTransformation(role, spec, original, specifier, action, option);
    } else if (["emphasis", "view.emphasis"].includes(role)) {
      spec = await CFC.EmphasisTransformation(role, spec, original, specifier, action, option);
    } else if (["title", "text.title", "view.title"].includes(role)) {
      spec = await CFC.TitleTransformation(role, spec, original, specifier, action, option);
    } else if (["interaction", "view.interaction"].includes(role)) {
      spec = await CFC.InteractionTransformation(role, spec, original, specifier, action, option);
    } else if (["text"].includes(role)) {

    } else if (["text.axis"].includes(role)) {

    } else if (["text.hAxis"].includes(role)) {

    } else if (["text.vAxis"].includes(role)) {

    } else if (["text.legend"].includes(role)) {

    }
  }
  return spec;
}

export { CFC, compilerCiceroToFronto };